import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Circle, MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import Control from 'react-leaflet-custom-control';
import { Browser, divIcon, LatLngBounds } from 'leaflet';
import MarkerClusterGroup from '@changey/react-leaflet-markercluster';
import DwellingCard from './DwellingCard';
import Alert from '../../../../Shared/components/Alert';
import { ZdsIcon, ZdsMapMarker } from '@zig-design-system/ui-components-react';
import { renderToStaticMarkup } from 'react-dom/server';
import ViewLinks from './ViewLinks';
import ViewService from '../../../services/ViewService';
import UrlService from '../../../services/UrlService';
const MapView = ({ units, searchRadius }) => {
    const map = useRef();
    const searchRadiusPathOptions = {
        fillOpacity: 0,
        color: '#0070c0',
    };
    useEffect(() => {
        const zdsClickEventHandler = (event) => {
            // Events to manipulate DOM elements.
            const target = event.target; /** Pure here for typecasting target */
            if (target.nodeName.toLowerCase() === 'zds-dwelling-card') {
                if (!event.detail.ctrlKey) {
                    ViewService.setRegularWidth();
                }
            }
        };
        const removeTabIndexes = () => {
            document.querySelectorAll('.marker-cluster-icon').forEach((cluster) => {
                cluster.removeAttribute('tabIndex');
            });
        };
        const removeTabIndexesInterval = window.setInterval(() => {
            removeTabIndexes();
        }, 500);
        const mapRef = map.current;
        if (mapRef) {
            mapRef.addEventListener('zdsClick', zdsClickEventHandler);
        }
        const timeout = setTimeout(() => {
            //Dispatch a resize event after loading to make sure the map is nicely rendered
            window.dispatchEvent(new Event('resize'));
        }, 500);
        return () => {
            clearTimeout(timeout);
            if (mapRef) {
                clearInterval(removeTabIndexesInterval);
                mapRef.removeEventListener('zdsClick', zdsClickEventHandler);
            }
        };
    }, []);
    useLayoutEffect(() => {
        ViewService.setFullWidth();
    });
    const markerIcon = divIcon({
        html: renderToStaticMarkup(React.createElement(ZdsMapMarker, null,
            React.createElement(ZdsIcon, { name: "home", size: "inherit" }))),
        iconSize: [30, 30],
        className: '',
    });
    const createClusterCustomIcon = (cluster) => {
        return divIcon({
            html: renderToStaticMarkup(React.createElement(ZdsMapMarker, { cluster: true },
                " ",
                cluster.getChildCount(),
                " ")),
            className: 'marker-cluster-icon',
            iconSize: [30, 30],
        });
    };
    const getBounds = () => {
        if (searchRadius) {
            // Earth's radius in meters
            const earthRadius = 6378137;
            const centerLat = typeof searchRadius.center.lat === 'number'
                ? searchRadius.center.lat
                : parseFloat(searchRadius.center.lat);
            const centerLng = typeof searchRadius.center.lng === 'number'
                ? searchRadius.center.lng
                : parseFloat(searchRadius.center.lng);
            // Convert radius from meters to degrees (approximately)
            const latRadius = (searchRadius.radius / earthRadius) * (180 / Math.PI);
            const lngRadius = (searchRadius.radius / (earthRadius * Math.cos((Math.PI * centerLat) / 180))) * (180 / Math.PI);
            return new LatLngBounds([
                [centerLat + latRadius, centerLng],
                [centerLat - latRadius, centerLng],
                [centerLat, centerLng + lngRadius],
                [centerLat, centerLng - lngRadius],
            ]);
        }
        return new LatLngBounds(filteredUnits.map((unit) => {
            return [unit._source.fields.location.lat, unit._source.fields.location.lon];
        }));
    };
    if (!units) {
        return null;
    }
    const filteredUnits = units.filter((unit) => {
        var _a, _b, _c, _d;
        return ((_b = (_a = unit._source.fields) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.lon) && ((_d = (_c = unit._source.fields) === null || _c === void 0 ? void 0 : _c.location) === null || _d === void 0 ? void 0 : _d.lat);
    });
    if (filteredUnits.length === 0) {
        return (React.createElement("div", { className: "sm:zds-offset-2 zds-col-8 zds-padding-top-4" },
            React.createElement(Alert, { title: "t(tx_zigrealestate.details.no_object_found)" })));
    }
    return (React.createElement("div", { ref: map, style: { position: 'sticky', top: '0px' } },
        React.createElement(MapContainer, { bounds: getBounds(), zoom: 5, scrollWheelZoom: true, style: { height: Browser.mobile ? '50em' : '100vh', width: '100%' }, dragging: !Browser.mobile, touchZoom: true },
            React.createElement(TileLayer, { attribution: '\u00A9 <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors', url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" }),
            React.createElement(Control, { prepend: true, position: 'topright', container: {
                    style: {
                        backgroundColor: 'white',
                        padding: '15px',
                        margin: '15px',
                        borderColor: 'white',
                        borderRadius: '10px',
                    },
                } },
                React.createElement("div", { className: "zds-flex zds-gap-x-6" },
                    React.createElement(ViewLinks, null))),
            searchRadius && (React.createElement(Circle, { center: searchRadius.center, pathOptions: searchRadiusPathOptions, radius: searchRadius.radius })),
            React.createElement(MarkerClusterGroup, { iconCreateFunction: createClusterCustomIcon, removeOutsideVisibleBound: true, maxClusterRadius: 50, chunkedLoading: true }, filteredUnits.map((unitHit) => {
                const unit = unitHit._source.fields;
                return (React.createElement(Marker, { key: unitHit._id, position: [unit.location.lat, unit.location.lon], icon: markerIcon, keyboard: false },
                    React.createElement(Popup, { offset: [0, -5], maxWidth: 300, autoClose: true, closeButton: false, minWidth: 300, maxHeight: 1000 },
                        React.createElement(DwellingCard, { unit: unit, url: UrlService.getAbsoluteUrlForPathSegment(unitHit._source.url), currentView: "map" }))));
            })))));
};
export default MapView;
